import * as Sentry from '@sentry/react';
import { config } from './runtime-config';

/**
 * Initialize Sentry error reporting
 */
export function initSentry() {
  if (!config.features.errorReporting) {
    return;
  }

  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) {
    console.warn('Sentry DSN not configured. Set VITE_SENTRY_DSN to enable error reporting.');
    return;
  }

  Sentry.init({
    dsn,
    environment: config.environment,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        // Only capture replays for errors in production
        maskAllText: config.environment === 'production',
        blockAllMedia: config.environment === 'production',
      }),
    ],
    // Performance monitoring
    tracesSampleRate: config.environment === 'production' ? 0.1 : 1.0,
    // Capture replays for 10% of sessions, 100% of error sessions
    replaysSessionSampleRate: config.environment === 'production' ? 0.1 : 1.0,
    replaysOnErrorSampleRate: 1.0,
    // Scrub sensitive data
    beforeSend(event) {
      // Remove sensitive data from event
      if (event.request?.data) {
        event.request.data = scrubSensitiveData(event.request.data);
      }
      if (event.extra) {
        event.extra = scrubSensitiveData(event.extra);
      }
      return event;
    },
  });
}

/**
 * Scrub sensitive data from objects before sending to Sentry
 */
function scrubSensitiveData(data: any): any {
  if (typeof data !== 'object' || data === null) {
    return data;
  }

  const sensitiveKeys = [
    'password',
    'token',
    'secret',
    'key',
    'auth',
    'api_key',
    'apikey',
    'email',
    'user_id',
    'userId',
    'phone',
    'ssn',
    'credit_card',
    'creditcard',
  ];

  if (Array.isArray(data)) {
    return data.map(scrubSensitiveData);
  }

  const scrubbed: any = {};
  for (const [key, value] of Object.entries(data)) {
    const lowerKey = key.toLowerCase();
    if (sensitiveKeys.some(sensitive => lowerKey.includes(sensitive))) {
      scrubbed[key] = '[REDACTED]';
    } else if (typeof value === 'object') {
      scrubbed[key] = scrubSensitiveData(value);
    } else {
      scrubbed[key] = value;
    }
  }

  return scrubbed;
}

/**
 * Capture exception with Sentry (if enabled)
 */
export function captureException(error: Error, context?: Record<string, any>) {
  if (!config.features.errorReporting) {
    return;
  }

  Sentry.captureException(error, {
    extra: context ? scrubSensitiveData(context) : undefined,
  });
}

/**
 * Capture message with Sentry (if enabled)
 */
export function captureMessage(message: string, level: Sentry.SeverityLevel = 'info', context?: Record<string, any>) {
  if (!config.features.errorReporting) {
    return;
  }

  Sentry.captureMessage(message, {
    level,
    extra: context ? scrubSensitiveData(context) : undefined,
  });
}

/**
 * Set user context for Sentry
 */
export function setUserContext(user: { id?: string; email?: string; [key: string]: any }) {
  if (!config.features.errorReporting) {
    return;
  }

  Sentry.setUser({
    id: user.id,
    // Don't send email in production for privacy
    email: config.environment === 'development' ? user.email : undefined,
  });
}

/**
 * Clear user context
 */
export function clearUserContext() {
  if (!config.features.errorReporting) {
    return;
  }

  Sentry.setUser(null);
}

// Export Sentry for direct access if needed
export { Sentry };
